import React, { useState, useEffect } from "react";
import MenuButtons from "../../components/MenuButtons";
import { useAuth } from "../../services/AuthContext";
import { useRefresh } from "../../services/RefreshContext";
import { useToast } from '../../services/ToastContext';
import Tooltip from "../../components/Tooltip";
import {
  fetchChildSavingsInfo,
  fetchChildTransactions,
  deleteChildGoal,
} from "../../services/Api";
import LoadingSpinner from "../../components/LoadingSpinner";
import DeletePopup from "../../components/DeletePopup";
import SavingsGoalForm from "./SavingsGoalForm";

const SavingsPage = () => {
  const { childID } = useAuth();
  const { refreshCounter, triggerRefresh } = useRefresh();
  const { showToast } = useToast();

  const [goals, setGoals] = useState([]);
  const [balance, setBalance] = useState("");
  const [firstName, setFirstName] = useState("");
  const [transactions, setTransactions] = useState("");
  const [isSpinnerLoading, setIsSpinnerLoading] = useState(true);

  const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
  const [deletingGoal, setDeletingGoal] = useState();
  const [isSavingsGoalPopupOpen, setIsSavingsGoalPopupOpen] = useState(false);
  const [goalToEdit, setGoalToEdit] = useState("");

  // Use useEffect to fetch data when the component loads
  useEffect(() => {
    setIsSpinnerLoading(true);
    const fetchData = async () => {
      try {
        const minDelay = new Promise((resolve) => setTimeout(resolve, 200));
        const [savingsRes, transactionsRes] = await Promise.all([
          fetchChildSavingsInfo(childID),
          fetchChildTransactions(childID),
          minDelay,
        ]);
        setGoals(savingsRes?.savingsGoals);
        setBalance(savingsRes?.childInfo.balance);
        setFirstName(savingsRes?.childInfo.firstName);
        setTransactions(transactionsRes?.transactions);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsSpinnerLoading(false);
      }
    };

    fetchData();
  }, [childID, refreshCounter]);

  const dateFormatter = new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });

  const handleDelete = async (id) => {
    await deleteChildGoal(id);
    setDeletingGoal("");
    setIsDeletePopupOpen(false);
    showToast("Goal removed");
    triggerRefresh();
  };

  const confirmDelete = (goal) => {
    setDeletingGoal(goal);
    setIsDeletePopupOpen(true);
  };

  const handleEdit = (e, goal) => {
    e.preventDefault();
    setGoalToEdit(goal);
    setIsSavingsGoalPopupOpen(true);
  };

  let moneyUsedUp = 0;

  return (
    <>
      <MenuButtons />
      <LoadingSpinner isLoading={isSpinnerLoading} />
      {/* add a div fade-in and conditional visible classes to get fade in for content working*/}
      <div className={`fade-in ${!isSpinnerLoading ? "visible" : ""}`}>
        <div className="savingsTransactionsContainer">
          <div className="savingsBox">
            <h2>Piggy Bank Balance</h2>
            <h3>${balance ?? "0.00"}</h3>
          </div>
          <div className="transaction-container">
            <h3 style={{ color: "white" }}>Recent Transactions</h3>
            {transactions && transactions.length !== 0 ? (
              <>
                {transactions.map((transaction) => (
                  <div className="transaction-item" key={transaction.ID}>
                    <div className="icon">
                      <span className={transaction.Amount > 0 ? "up" : "down"}>
                        {transaction.Amount > 0 ? "↑" : "↓"}
                      </span>
                    </div>

                    <div className="transaction-content">

                      <div className="transaction-item-margins">
                        <div className="description">{transaction.Description}</div>
                      </div>

                      <div className="transaction-item-margins">
                        <div
                          className={`amount ${transaction.Amount > 0 ? "positive" : "negative"}`}
                        >
                          {transaction.Amount
                            ? `${transaction.Amount < 0 ? "- " : "+ "}$${Math.abs(
                              transaction.Amount
                            ).toFixed(2)}`
                            : "$0.00"}
                        </div>
                      </div>

                      <div className="transaction-item-margins">
                        <div className="date">
                          {transaction.Date
                            ? dateFormatter.format(new Date(transaction.Date))
                            : ""}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </>
            ) : (
              <>
                <br />
                <div style={{ height: "100%", display: "flex", justifyContent: "center" }}>                
                  <h4 className="text-muted text-center">No transactions recorded</h4>
                </div>
              </>
            )}
          </div>
        </div>

        <div style={{ marginTop: "30px", textAlign: "center" }}>
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            <h3 style={{ marginBottom: "0px"}}>Savings Goals</h3>
            {/* <div className="cardHeaderButton"> */}
              <button
                className="confirm-btn"
                onClick={() => setIsSavingsGoalPopupOpen(true)}
                style={{
                  width: "60px",
                  fontSize: "20px",
                  margin: "0px",
                  marginLeft: "15px",
                }}
              >
                <Tooltip
                  content={<i className="fas fa-plus fa-lg green" />}
                  description="Add Goal"
                  isCustom={true}
                />
              </button>
            </div>
            {/* <button className="globalButton" onClick={() => setIsSavingsGoalPopupOpen(true)}>
              Add a New Savings Goal
            </button> */}
          {/* </div>           */}
          {goals && goals.length > 0 ? (
            <div className="savingsGoalContainer">
              {goals.map((goal) => {
                const available = Math.max(balance - moneyUsedUp, 0);
                const allocated = Math.min(available, goal?.goalAmount);
                moneyUsedUp += allocated;
                const progressPercent = goal?.goalAmount
                  ? (allocated / goal?.goalAmount) * 100
                  : 0;

                return (
                  <div key={goal.id} className="savingsGoalBox">
                    <div className="goal-info">
                      <h4>{goal.name}</h4>
                      <p>Goal: ${goal.goalAmount}</p>
                      <p>                        
                        {balance && goal.goalAmount
                          ? `Saved: $${allocated?.toFixed(2)}`
                          : "0.00"}
                      </p>
                      <div className="progress">
                        <div
                          className="goal-progress-bar"
                          style={{ width: `${progressPercent}%` }}
                        />
                      </div>
                      <p>
                        {progressPercent < 100
                          ? Math.floor(progressPercent)
                          : 100}
                        %
                      </p>
                    </div>
                    <div className="button-container">
                      <button
                        className="edit-btn"
                        onClick={(e) => handleEdit(e, goal)}
                      >
                        Edit
                      </button>
                      <button
                        className="delete-btn"
                        style={{ padding: "5px", maxHeight: "2em" }}
                        onClick={() => confirmDelete(goal)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <>
              <br />
              <div className="pb-5" style={{ height: "100%", width: "100%", display: "flex", justifyContent: "center" }}>                
                <h4>No savings goals added</h4>
              </div>
            </>              
          )}
        </div>
      </div>


      <DeletePopup
        isOpen={isDeletePopupOpen}
        onClose={() => {
          setIsDeletePopupOpen(false);
          setDeletingGoal("");
        }}
        removeID={deletingGoal?.id}
        content={
          <>
            <h2 className="text-xl font-semibold mb-4 text-center">
              Are you sure you want to delete:
            </h2>

            <p className="text-center mb-6 fs-5">
              "{deletingGoal?.name}" with goal amount of ${deletingGoal?.goalAmount}?
            </p>
          </>
        }
        onConfirm={handleDelete}
      />
      <SavingsGoalForm 
        isOpen={isSavingsGoalPopupOpen}
        onClose={() => {
          setIsSavingsGoalPopupOpen(false);
          setGoalToEdit("");
        }}
        goalToEdit={goalToEdit}
      />
    </>
  );
};

export default SavingsPage;
